import { CP_AdvSiteName, CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { VASTTagAdUnit } from "@mp-commons/modules/ads/AdUnit.js";
import { ContentResumePolicy } from "@mp-commons/modules/ads/ContentResumePolicy.js";
import { AdUnitApplicationPolicy, VASTAdUnitConfig } from "@mp-commons/modules/ads/ima/MultipleVASTImaAdCallController.js";
import { ProductAdsProvider, productAdsProvider } from "./AdsProviderBuilder.js";
import { VideoInfoRawData } from "@mp-commons/model/video/VideoInfo.js";
import { MediaId } from "@mp-commons/model/MediaInfo.js";


/**
 * Builds a configuration array for VAST ad units (preroll, spreroll, postroll) based on the provided media elements and parameters.
 *
 * This function determines which ad units to include and how to configure them according to the embed type, advertising settings,
 * video policy, and site context. It handles special cases for different embed types (e.g., FANPLAYER, VIDEO_TINT, GALLERY) and
 * applies exclusion policies for non-advertisable media.
 *
 * @param mediaElements - Array of video metadata objects to be considered for ad insertion.
 * @param params - Configuration parameters:
 *   @property embedBehaviour - Indicates if embed behaviour is enabled for the player.
 *   @property withoutAdvertising - If true, disables all advertising and returns an empty array.
 *   @property sitePage - The site context for ad provider selection.
 *   @property embedType - The type of embed/player being used.
 *
 * @returns An array of `VASTAdUnitConfig` objects representing the ad units to be configured for the player.
 */
export function buildVastAdUnitsConfig(mediaElements: VideoInfoRawData[], params: { embedBehaviour: boolean, withoutAdvertising: boolean, site: CP_AdvSiteName, embedType: CP_EmbedType, }): VASTAdUnitConfig[] {

  const { site, embedType, embedBehaviour, withoutAdvertising } = params;

  const provider: ProductAdsProvider = productAdsProvider(site);

  if (withoutAdvertising || mediaElements.length == 0) {
    return [];
  } else {

    const mediaNotAdvertisable = embedType === CP_EmbedType.FANPLAYER || embedType === CP_EmbedType.VIDEO_TINT || embedType === CP_EmbedType.GALLERY ? [] : mediaElements.filter(m => m.disableAdv);

    // fanplayer con embed behavior == false e tint --> playlist
    const playlistBehaviour = (embedType == CP_EmbedType.FANPLAYER && !embedBehaviour) || embedType == CP_EmbedType.VIDEO_TINT;

    // PREROLL ---> TINT, FANPLAYER, ALTRIMENTI SE ADVERTISABLE
    const prerollConfigBuilderFn: (adUnit: VASTTagAdUnit) => VASTAdUnitConfig = (adUnit: VASTTagAdUnit) => {
      const usingExclude = playlistBehaviour && mediaNotAdvertisable.length > 0;
      const prerollApplicationPolicy = usingExclude
        ? AdUnitApplicationPolicy.EXCLUDE_RESOURCES
        : AdUnitApplicationPolicy.ALL_RESOURCES;

      const mediaToExclude = usingExclude ? mediaNotAdvertisable.map(e => e.id as MediaId) : undefined;

      return {
        unit: adUnit,
        applicationPolicy: prerollApplicationPolicy,
        contentResumePolicy: ContentResumePolicy.RESUME_ALWAYS,
        resources: mediaToExclude
      }
    }

    const adPrerolls = (playlistBehaviour || (!mediaElements[0].disableAdv || embedType == CP_EmbedType.FANPLAYER) ? provider.prerolls(embedType) : [])
      .map(unit => prerollConfigBuilderFn(unit))


    // SPREROLL ---> FANPLAYER, GALLERY, ALTRIMENTI (SE VIDEO_TESTO O VIDEO && COOKIST_COM && ADVERTISABLE)
    const sprerollConfigBuilderFn: (adUnit: VASTTagAdUnit) => VASTAdUnitConfig = (adUnit: VASTTagAdUnit) => {

      return {
        unit: adUnit,
        applicationPolicy: playlistBehaviour ? AdUnitApplicationPolicy.FIRST_MEDIA : AdUnitApplicationPolicy.ALL_RESOURCES,
        contentResumePolicy: ContentResumePolicy.RESUME_ALWAYS
      }
    }

    const adSprerolls = ((embedType == CP_EmbedType.FANPLAYER || embedType == CP_EmbedType.GALLERY ||
      ((embedType == CP_EmbedType.VIDEO || embedType == CP_EmbedType.VIDEO_TESTO) && site == CP_AdvSiteName.COOKISTWOW)) ?
      provider.sprerolls(embedType) : [])
      .map(unit => sprerollConfigBuilderFn(unit))

    if (adSprerolls[adSprerolls.length - 1] && (embedType == CP_EmbedType.FANPLAYER || embedType == CP_EmbedType.GALLERY)) {
      adSprerolls[adSprerolls.length - 1].contentResumePolicy = ContentResumePolicy.RESUME_ON_SUCCESS;
    }

    // POSTROLL ---> FANPLAYER EMBED BEHAVIOUR, ALTRIMENTI SE ADVERTISABLE 

    const postrollConfigBuilderFn: (adUnit: VASTTagAdUnit) => VASTAdUnitConfig = (adUnit: VASTTagAdUnit) => {
      return {
        unit: adUnit,
        applicationPolicy: playlistBehaviour ? AdUnitApplicationPolicy.INCLUDE_RESOURCES : AdUnitApplicationPolicy.ALL_RESOURCES,
        contentResumePolicy: ContentResumePolicy.NEVER_RESUME,
      }
    }

    const adPostrolls = ((embedType == CP_EmbedType.FANPLAYER && embedBehaviour) || (!mediaElements[0].disableAdv) ?
      provider.postrolls(embedType) : [])
      .map(unit => postrollConfigBuilderFn(unit))

    return [...adPrerolls, ...adSprerolls, ...adPostrolls];

  }

}
